/** Seeded 2D/3D simplex + fbm + worley. Deterministic, allocation free. */
const F2 = 0.5 * (Math.sqrt(3) - 1), G2 = (3 - Math.sqrt(3)) / 6;
const GRAD2 = new Float32Array([1,1, -1,1, 1,-1, -1,-1, 1,0, -1,0, 0,1, 0,-1]);

export class Noise {
  constructor(seed = 1) {
    this.p = new Uint8Array(512);
    const perm = new Uint8Array(256);
    for (let i = 0; i < 256; i++) perm[i] = i;
    let s = seed >>> 0 || 1;
    const rnd = () => { s ^= s << 13; s >>>= 0; s ^= s >> 17; s ^= s << 5; s >>>= 0; return s / 4294967296; };
    for (let i = 255; i > 0; i--) { const j = (rnd() * (i + 1)) | 0; const t = perm[i]; perm[i] = perm[j]; perm[j] = t; }
    for (let i = 0; i < 512; i++) this.p[i] = perm[i & 255];
  }
  /** Simplex noise in [-1,1]. */
  n2(xin, yin) {
    const p = this.p;
    const s = (xin + yin) * F2;
    const i = Math.floor(xin + s), j = Math.floor(yin + s);
    const t = (i + j) * G2;
    const x0 = xin - (i - t), y0 = yin - (j - t);
    let i1, j1;
    if (x0 > y0) { i1 = 1; j1 = 0; } else { i1 = 0; j1 = 1; }
    const x1 = x0 - i1 + G2, y1 = y0 - j1 + G2;
    const x2 = x0 - 1 + 2 * G2, y2 = y0 - 1 + 2 * G2;
    const ii = i & 255, jj = j & 255;
    let n0 = 0, n1 = 0, n2 = 0;
    let t0 = 0.5 - x0 * x0 - y0 * y0;
    if (t0 > 0) { const g = (p[ii + p[jj]] & 7) * 2; t0 *= t0; n0 = t0 * t0 * (GRAD2[g] * x0 + GRAD2[g + 1] * y0); }
    let t1 = 0.5 - x1 * x1 - y1 * y1;
    if (t1 > 0) { const g = (p[ii + i1 + p[jj + j1]] & 7) * 2; t1 *= t1; n1 = t1 * t1 * (GRAD2[g] * x1 + GRAD2[g + 1] * y1); }
    let t2 = 0.5 - x2 * x2 - y2 * y2;
    if (t2 > 0) { const g = (p[ii + 1 + p[jj + 1]] & 7) * 2; t2 *= t2; n2 = t2 * t2 * (GRAD2[g] * x2 + GRAD2[g + 1] * y2); }
    return 70 * (n0 + n1 + n2);
  }
  fbm(x, y, oct = 4, lac = 2.0, gain = 0.5) {
    let a = 1, f = 1, sum = 0, norm = 0;
    for (let i = 0; i < oct; i++) { sum += a * this.n2(x * f, y * f); norm += a; a *= gain; f *= lac; }
    return sum / norm;
  }
  ridged(x, y, oct = 4, lac = 2.0, gain = 0.5) {
    let a = 1, f = 1, sum = 0, norm = 0;
    for (let i = 0; i < oct; i++) { sum += a * (1 - Math.abs(this.n2(x * f, y * f))); norm += a; a *= gain; f *= lac; }
    return (sum / norm) * 2 - 1;
  }
  /** Cheap cellular / worley distance field in [0,1]. */
  worley(x, y, scale = 1) {
    x *= scale; y *= scale;
    const xi = Math.floor(x), yi = Math.floor(y);
    let best = 8;
    for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) {
      const cx = xi + dx, cy = yi + dy;
      const h = this.p[(cx & 255) + this.p[cy & 255]];
      const px = cx + (h & 15) / 15, py = cy + ((h >> 4) & 15) / 15;
      const d = (px - x) * (px - x) + (py - y) * (py - y);
      if (d < best) best = d;
    }
    return Math.min(1, Math.sqrt(best));
  }
}
export const noise = new Noise(9137);
